/* eslint-disable react/require-default-props */
import React, { FC, useState, useEffect, useRef, KeyboardEventHandler } from 'react';
import handleKeyPress from '../../common/handleKeyPress';
import * as Styled from './styles';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { EventKey } from '../../constants/eventKey';

type Option = {
    name: string;
    displayName?: string;
    value: string;
};

interface IProps {
    options: Array<Option>;
    defaultValue?: Option;
    label?: string;
    width?: number;
    onSelect: ({ name, value }: Option) => {};
}

const Dropdown: FC<IProps> = ({ options, defaultValue, label = '', width = 70, onSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(defaultValue || options[0]);
    const [currentFocus, setCurrentFocus] = useState(-1);
    const selectEl = useRef(null);
    const currentOption = useRef(null);
    const dropdownList = useRef(null);
    const prefix = label.trim().toLowerCase();

    useEffect(() => {
        onSelect(selectedOption);
    }, [selectedOption]);

    useEffect(() => {
        isOpen && currentOption.current.focus();
    }, [isOpen]);

    useEffect(() => {
        if (currentFocus !== -1 && dropdownList.current) {
            (dropdownList.current.children[currentFocus] as HTMLElement).focus();
        }
    }, [currentFocus]);

    const closeModal = (setFocus = true) => {
        setIsOpen(false);

        if (setFocus) {
            selectEl.current.focus();
        }
    };

    useOnClickOutside(selectEl, () => closeModal(false));

    const selectOption = (option: Option) => {
        setSelectedOption(option);
        closeModal();
    };

    const handleDropdownKeyEvent: KeyboardEventHandler<HTMLUListElement | HTMLLIElement> = (e: any): void => {
        if (!dropdownList.current) {
            return;
        }
        const totalItems = dropdownList.current.children.length;

        switch (e.key) {
            case EventKey.Tab:
            case EventKey.Escape:
                if (isOpen) {
                    closeModal();
                }

                e.preventDefault();
                break;
            case EventKey.Home:
            case EventKey.End:
            case EventKey.A:
                if (e.key === EventKey.Home) {
                    // Home
                    selectOption(options[0]);
                } else {
                    // End
                    selectOption(options[options.length - 1]);
                }
                e.preventDefault();
                break;
            case EventKey.ArrowUp:
            case EventKey.ArrowDown:
                if (document.activeElement === dropdownList.current) {
                    const focus = e.key === EventKey.ArrowUp ? totalItems - 1 : 0;
                    setCurrentFocus(focus);
                } else {
                    (dropdownList.current.children[currentFocus] as HTMLElement).blur();

                    const directionEndIdx = e.key === EventKey.ArrowUp ? 0 : totalItems - 1;

                    const oppositeEndIdx = e.key === EventKey.ArrowUp ? totalItems - 1 : 0;

                    const nextItemIdx = e.key === EventKey.ArrowUp ? currentFocus - 1 : currentFocus + 1;

                    const newFocus = currentFocus === directionEndIdx ? oppositeEndIdx : nextItemIdx;
                    setCurrentFocus(newFocus);
                }
                e.preventDefault();
                break;
            default:
                e.preventDefault();
                break;
        }
    };

    const handleDropdownMenuOpen: KeyboardEventHandler<HTMLDivElement> = (e): void => {
        if (e.key === EventKey.Enter && !isOpen) {
            setIsOpen(true);
            setCurrentFocus(0);
            return;
        }

        if (e.key === EventKey.Escape && isOpen) {
            closeModal();
            setCurrentFocus(-1);
            return;
        }

        if (isOpen && selectEl.current && !selectEl.current.contains(e.target as Node)) {
            closeModal();
            setCurrentFocus(-1);
        }
    };

    return (
        <Styled.DropdownWrapper>
            {label && <Styled.DropdownLabel id={`${prefix}-dropdown-label`}>{label}</Styled.DropdownLabel>}
            <Styled.DropdownSelector
                data-test-id={`${prefix}-dropdown-selector`}
                tabIndex={0}
                width={width}
                onClick={() => {
                    if (!isOpen) {
                        setCurrentFocus(0);
                    } else {
                        setCurrentFocus(-1);
                    }
                    setIsOpen(!isOpen);
                }}
                onKeyDown={(e) => handleKeyPress(e, () => handleDropdownMenuOpen(e), true)}
                aria-expanded={isOpen}
                aria-controls={`${prefix}-dropdown-options`}
                aria-labelledby={`${prefix}-dropdown-label`}
                ref={selectEl}
            >
                {isOpen && (
                    <Styled.Dropdown
                        id={`${prefix}-dropdown-options`}
                        data-test-id={`${prefix}-dropdown-options`}
                        width={width}
                        ref={dropdownList}
                    >
                        {options.map((option: Option) => (
                            <Styled.DropdownItem
                                tabIndex={0}
                                data-test-id={`${prefix}-${option.value}`}
                                key={`list-item-${option.value.trim().toLowerCase()}`}
                                onClick={() => selectOption(option)}
                                onKeyDown={(e) => {
                                    e.key === 'Escape'
                                        ? closeModal()
                                        : handleKeyPress(e, () => selectOption(option), true);
                                }}
                                ref={option.value === selectedOption.value ? currentOption : null}
                                onKeyDownCapture={handleDropdownKeyEvent}
                            >
                                <Styled.DropdownText>{option.name}</Styled.DropdownText>
                            </Styled.DropdownItem>
                        ))}
                    </Styled.Dropdown>
                )}
                <Styled.DropdownText>
                    <Styled.DropdownTextSpan>
                        {selectedOption.displayName || selectedOption.name}
                    </Styled.DropdownTextSpan>
                    <Styled.Icon icon="chevron-down" />
                </Styled.DropdownText>
            </Styled.DropdownSelector>
        </Styled.DropdownWrapper>
    );
};

export default Dropdown;
